import {UserSearchAutocomplete} from '@hconnect/common/components/formFields'
import {ControlledFormField} from '@hconnect/common/types'
import {Chip} from '@mui/material'
import {useState} from 'react'
import {Controller, FieldValues} from 'react-hook-form'

import {useFormInputLabelWithSuffix, useMachineSentryUserSearch} from '../../hooks'

type User = {
  id: string
  name: string
}

type MachineSentryUserSearchFormFieldProps<TField extends FieldValues> =
  ControlledFormField<TField> & {
    plantUpmId: string
    pleaseStartTypingLabel?: string
  }

export const MachineSentryUserSearchFormField = <TField extends FieldValues>({
  plantUpmId,
  label,
  name,
  control,
  disabled,
  readOnly,
  optional,
  pleaseStartTypingLabel
}: MachineSentryUserSearchFormFieldProps<TField>) => {
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined)

  const {data: userSuggestions, isFetching: isUsersLoading} = useMachineSentryUserSearch(
    plantUpmId,
    searchTerm
  )

  const fieldDisabled = disabled || readOnly || !plantUpmId
  const inputLabel = useFormInputLabelWithSuffix({label, optional})
  const dataTestId = `machine-sentry-user-search-field-${name}`

  return (
    <Controller
      name={name}
      disabled={fieldDisabled}
      control={control}
      render={({field, fieldState}) => {
        const filterAvailableUsers = (suggestions, currentValues) =>
          ((suggestions as User[]) ?? []).filter(
            (user) => !(currentValues || []).find((assigned: User) => assigned.id === user.id)
          )

        return (
          <UserSearchAutocomplete
            multiple
            name={name}
            value={field.value ?? []}
            label={inputLabel}
            required={!optional}
            disabled={fieldDisabled}
            forcePopupIcon={false}
            helperText={fieldState.error?.message}
            error={!!fieldState.error?.message}
            disableCloseOnSelect={false}
            placeholder={pleaseStartTypingLabel}
            data-test-id={dataTestId}
            onChange={(_, newValue) => {
              if (!Array.isArray(newValue)) {
                field.onChange([])
                return
              }

              field.onChange(newValue)
            }}
            onInputChange={setSearchTerm}
            query={{
              data: filterAvailableUsers(userSuggestions, field.value),
              isFetching: isUsersLoading
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option: User, index: number) => {
                const props = getTagProps({index})
                return (
                  <Chip
                    {...props}
                    variant="outlined"
                    size="small"
                    key={option.id}
                    label={option.name}
                    onDelete={props.onDelete}
                    data-test-id={`${dataTestId}-tag-${option.id}`}
                  />
                )
              })
            }
          />
        )
      }}
    />
  )
}
